// src/components/Profile/Profile.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Save, 
  Camera, 
  User, 
  Mail, 
  Globe, 
  MapPin, 
  Building, 
  Clock,
  Check,
  AlertTriangle,
  Shield,
  Briefcase,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isSheetsSurface } from '../../utils/productBranding';
import toast from 'react-hot-toast';
import OrganizationMembershipManager from './OrganizationMembershipManager';
import organizationService, { normalizeOrganizationName } from '../../services/organizationService';

const ORGANIZATION_TYPES = ['Business', 'Institution', 'Non-Profit'];
const INSTITUTION_TYPES = ['University', 'College', 'School'];

const Profile = ({ isModal = false, onClose }) => {
  const { user, userProfile, updateProfile, updateProfileInfo } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  const [profileData, setProfileData] = useState({
    displayName: userProfile?.displayName || user?.displayName || '',
    email: user?.email || '',
    photoURL: userProfile?.photoURL || user?.photoURL || '',
    bio: userProfile?.profile?.bio || '',
    organization: userProfile?.profile?.organization || '',
    organizationType: userProfile?.profile?.organizationType || '',
    institutionType: userProfile?.profile?.institutionType || '',
    organizationName: userProfile?.profile?.organizationName || userProfile?.profile?.organization || '',
    position: userProfile?.profile?.position || '',
    organizationEmail: userProfile?.profile?.organizationEmail || '',
    website: userProfile?.profile?.website || '',
    location: userProfile?.profile?.location || '',
    timezone: userProfile?.profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  const fileInputRef = useRef(null);
  const isSheetsProduct = isSheetsSurface();

  useEffect(() => {
    setProfileData({
      displayName: userProfile?.displayName || user?.displayName || '',
      email: user?.email || '',
      photoURL: userProfile?.photoURL || user?.photoURL || '',
      bio: userProfile?.profile?.bio || '',
      organization: userProfile?.profile?.organization || '',
      organizationType: userProfile?.profile?.organizationType || '',
      institutionType: userProfile?.profile?.institutionType || '',
      organizationName: userProfile?.profile?.organizationName || userProfile?.profile?.organization || '',
      position: userProfile?.profile?.position || '',
      organizationEmail: userProfile?.profile?.organizationEmail || '',
      website: userProfile?.profile?.website || '',
      location: userProfile?.profile?.location || '',
      timezone: userProfile?.profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }, [user, userProfile]);

  const tabs = [
    { id: 'general', label: 'General Information', icon: User },
    { id: 'account', label: 'Account Details', icon: Shield },
  ];

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver', 
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
  ];

  const handleInputChange = (field, value) => {
    setProfileData((prev) => {
      const next = {
        ...prev,
        [field]: value
      };

      if (field === 'organizationType' && value !== 'Institution') {
        next.institutionType = '';
      }

      if (field === 'organizationName') {
        next.organization = value;
      }

      return next;
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData(prev => ({
          ...prev,
          photoURL: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      // Update display name and photo in Firebase Auth
      if (profileData.displayName !== user?.displayName || profileData.photoURL !== user?.photoURL) {
        await updateProfile(profileData.displayName, profileData.photoURL);
      }

      // Update profile information in Firestore
      await updateProfileInfo({
        displayName: profileData.displayName,
        bio: profileData.bio,
        organization: profileData.organizationName,
        organizationType: profileData.organizationType,
        institutionType: profileData.institutionType,
        organizationName: profileData.organizationName,
        position: profileData.position,
        organizationEmail: profileData.organizationEmail,
        website: profileData.website,
        location: profileData.location,
        timezone: profileData.timezone
      });

      // Lazy-convert legacy free-text organisation details on the next save.
      if (profileData.organizationName.trim() && profileData.organizationType && profileData.organizationEmail.trim()) {
        try {
          const memberships = await organizationService.getUserMemberships(user.uid);
          const membershipOrganizations = await organizationService.getOrganizationsByIds(
            memberships.map((item) => item.organizationId)
          );
          const normalizedLegacyName = normalizeOrganizationName(profileData.organizationName);
          const alreadyRequested = membershipOrganizations.some(
            (organization) => normalizeOrganizationName(organization.name) === normalizedLegacyName
          );
          if (!alreadyRequested) {
            const matches = await organizationService.searchOrganizations(profileData.organizationName);
            const exactMatch = matches.find(
              (organization) => normalizeOrganizationName(organization.name) === normalizedLegacyName
            );
            if (exactMatch) {
              await organizationService.requestMembership({
                organizationId: exactMatch.id,
                organizationEmail: profileData.organizationEmail,
                position: profileData.position
              });
            } else {
              await organizationService.createOrganizationRequest({
                name: profileData.organizationName,
                type: profileData.organizationType,
                institutionType: profileData.institutionType,
                organizationEmail: profileData.organizationEmail,
                position: profileData.position,
                website: profileData.website,
                location: profileData.location
              });
            }
          }
        } catch (organizationError) {
          toast.error(organizationError?.message || 'Profile saved, but the organisation request could not be submitted.');
        }
      }

      toast.success('Profile updated successfully!');
      if (isModal && onClose) {
        onClose();
      }
    } catch (error) {
      toast.error('Failed to update profile');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = () => {
    const name = profileData.displayName || user?.email || 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const renderGeneralTab = () => (
    <div className="space-y-6">
      {/* Profile Picture Section */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Picture</h3>
        <div className="flex items-center space-x-6">
          <div className="relative">
            {profileData.photoURL ? (
              <img 
                src={profileData.photoURL} 
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="w-24 h-24 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                <span className="text-white text-2xl font-bold">{getInitials()}</span>
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full hover:bg-indigo-700 transition-colors shadow-lg"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-900">Change photo</h4>
            <p className="text-sm text-gray-500 mb-2">JPG, PNG or GIF. Max size 5MB.</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Upload new picture
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Full Name
            </label>
            <input
              type="text"
              value={profileData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                {user?.emailVerified ? (
                  <div className="flex items-center text-green-600">
                    <Check className="w-4 h-4 mr-1" />
                    <span className="text-xs">Verified</span>
                  </div>
                ) : (
                  <div className="flex items-center text-orange-600">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    <span className="text-xs">Unverified</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              value={profileData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Tell us a bit about yourself..."
            />
          </div>
        </div>
      </div>

      {/* Professional Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Professional Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4 inline mr-2" />
              Organisation Type
            </label>
            <select
              value={profileData.organizationType}
              onChange={(e) => handleInputChange('organizationType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Select organisation type</option>
              {ORGANIZATION_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {profileData.organizationType === 'Institution' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building className="w-4 h-4 inline mr-2" />
                Institution Type
              </label>
              <select
                value={profileData.institutionType}
                onChange={(e) => handleInputChange('institutionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select institution type</option>
                {INSTITUTION_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4 inline mr-2" />
              Organisation Name
            </label>
            <input
              type="text"
              value={profileData.organizationName}
              onChange={(e) => handleInputChange('organizationName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Company, institution, or non-profit"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Briefcase className="w-4 h-4 inline mr-2" />
              Position
            </label>
            <input
              type="text"
              value={profileData.position}
              onChange={(e) => handleInputChange('position', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Your role or title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Organisation Email
            </label>
            <input
              type="email"
              value={profileData.organizationEmail}
              onChange={(e) => handleInputChange('organizationEmail', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="name@organisation.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="w-4 h-4 inline mr-2" />
              Website
            </label>
            <input
              type="url"
              value={profileData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="https://your-website.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-2" />
              Location
            </label>
            <input
              type="text"
              value={profileData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="City, Country"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-2" />
              Timezone
            </label>
            <select
              value={profileData.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <OrganizationMembershipManager />
    </div>
  );

  const renderAccountTab = () => (
    <div className="space-y-6">
      {/* Account Overview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Account Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
            <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">{user?.uid}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isSheetsProduct ? 'ParrotSheets Plan' : 'Account Type'}
            </label>
            <div className="flex items-center">
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                {userProfile?.subscription?.plan?.toUpperCase() || 'FREE'}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Authentication Provider</label>
            <p className="text-sm text-gray-900 capitalize">{userProfile?.provider || 'Email'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Verification</label>
            <div className="flex items-center">
              {user?.emailVerified ? (
                <span className="flex items-center text-green-600 text-sm">
                  <Check className="w-4 h-4 mr-1" />
                  Verified
                </span>
              ) : (
                <span className="flex items-center text-orange-600 text-sm">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Not Verified
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Created</label>
            <p className="text-sm text-gray-900">{formatDate(userProfile?.createdAt || userProfile?.metadata?.creationTime)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
            <p className="text-sm text-gray-900">{formatDate(userProfile?.lastLoginAt || userProfile?.metadata?.lastSignInTime)}</p>
          </div>
        </div>
      </div>

      {/* Storage Usage */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Storage Usage</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Documents</span>
            <span className="text-sm font-medium">{userProfile?.subscription?.documentsCount || 0} / {userProfile?.subscription?.maxDocuments || 100}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Storage</span>
            <span className="text-sm font-medium">
              {((userProfile?.subscription?.storageUsed || 0) / 1024 / 1024).toFixed(2)} MB / 
              {((userProfile?.subscription?.maxStorage || 1024 * 1024 * 1024) / 1024 / 1024 / 1024).toFixed(1)} GB
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-indigo-600 h-2 rounded-full" 
              style={{ 
                width: `${Math.min((userProfile?.subscription?.storageUsed || 0) / (userProfile?.subscription?.maxStorage || 1024 * 1024 * 1024) * 100, 100)}%` 
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Provider Data */}
      {userProfile?.providerData && userProfile.providerData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Connected Accounts</h3>
          <div className="space-y-3">
            {userProfile.providerData.map((provider, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 capitalize">
                    {provider.providerId.replace('.com', '')}
                  </p>
                  <p className="text-sm text-gray-600">{provider.email}</p>
                </div>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                  Connected
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={isModal ? 'fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 p-4' : 'space-y-6'}>
      <div className={isModal ? 'max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl' : 'space-y-6'}>
        <div className={isModal ? 'max-h-[90vh] overflow-y-auto p-6' : 'space-y-6'}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {isModal ? (
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
                  <p className="text-gray-600">Manage your personal information and account details</p>
                </div>
              ) : (
                <>
                  <Link
                    to="/settings"
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Link>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
                    <p className="text-gray-600">Manage your personal information and account details</p>
                  </div>
                </>
              )}
            </div>
            {isModal && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center space-x-4">
              {profileData.photoURL ? (
                <img 
                  src={profileData.photoURL} 
                  alt="Profile"
                  className="w-16 h-16 rounded-full object-cover border-2 border-white"
                />
              ) : (
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center border-2 border-white">
                  <span className="text-white text-xl font-bold">{getInitials()}</span>
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold">{profileData.displayName || 'User'}</h2>
                <p className="text-indigo-100">{profileData.email}</p>
                {(profileData.organizationName || profileData.organization) && (
                  <p className="text-indigo-200 text-sm">{profileData.organizationName || profileData.organization}</p>
                )}
                {profileData.position && (
                  <p className="text-indigo-100 text-sm">{profileData.position}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                        ${activeTab === tab.id
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'general' && renderGeneralTab()}
              {activeTab === 'account' && renderAccountTab()}
            </div>

            {activeTab === 'general' && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
